import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { parseCharacters } from "@/utils/format";
import type { FormField, FormPage } from "../../types";
import type { ConditionData } from "../../components/FieldConfigModal.utils";
import {
  DATE_TYPE_WITH_TIME,
  DATE_TYPE_WITHOUT_TIME,
  type BaseField,
  type Config,
} from "../types";
import { defaultValueConverter, isYearPrecision } from "./format-default-value";

dayjs.extend(customParseFormat);

export { defaultValueConverter };

const fieldTypeConverter = (
  type: string,
  includeTime: boolean | undefined,
  datePrecision?: "full" | "year",
): string => {
  if ((type === "date" || type === "datetime") && datePrecision === "year") {
    return "year";
  }
  if (includeTime && DATE_TYPE_WITH_TIME[type])
    return DATE_TYPE_WITH_TIME[type];
  if (!includeTime && DATE_TYPE_WITHOUT_TIME[type])
    return DATE_TYPE_WITHOUT_TIME[type];
  return type;
};

const decimalsFormat = (precision: number | null | undefined) =>
  precision === 0 || precision == null ? null : precision;

const allowDecimalsFormat = (precision: number | null) => precision !== 0;

const stringToArray = (str: string) =>
  str.split(" / ").map((opcion) => parseCharacters(opcion.trim()));

const normalizeRef = (s?: string | null) =>
  parseCharacters((s ?? "").toString().trim());

export function buildValidFieldRefs(form: FormPage[]): Set<string> {
  const refs = new Set<string>();
  for (const page of form) {
    for (const section of page.sections) {
      for (const f of section.fields) {
        if (f?.name) refs.add(normalizeRef(f.name));
        if (f?.technicalKey) refs.add(normalizeRef(f.technicalKey));
      }
    }
  }
  return refs;
}

function findCondField(
  form: FormPage[],
  fieldId: string | undefined,
): FormField | undefined {
  for (const p of form) {
    for (const s of p.sections) {
      for (const f of s.fields) {
        if (normalizeRef(f.name) === normalizeRef(fieldId)) return f;
      }
    }
  }
  return undefined;
}

function resolveConditionValue(
  condField: FormField | undefined,
  compareInput: string,
  compareDate: string | Date,
): string | string[] | null | Date {
  if (condField?.type === "select" && condField?.valueSource === "manual") {
    return stringToArray(compareInput);
  }
  if (condField?.type === "select" && condField?.valueSource === "database") {
    return compareInput;
  }
  return compareInput || compareDate || null;
}

function resolveFieldReference(selectedValue: ConditionData["selectedValue"]) {
  if (selectedValue?.node_id || selectedValue?.output_name) {
    return (
      selectedValue?.output_name ||
      selectedValue?.id ||
      "unknown_field"
    ).trim();
  }
  if (selectedValue?.id) {
    return normalizeRef(selectedValue.id);
  }
  return "unknown_field";
}

function mapConditionToField(
  c: { data: ConditionData },
  field: FormField,
  form: FormPage[],
) {
  const { selectedOperation, selectedValue, compareInput, compareDate } =
    c.data;
  const condField = findCondField(form, selectedValue?.id);
  const value = resolveConditionValue(
    condField,
    compareInput ?? "",
    compareDate ?? "",
  );
  // External variables (e.g. a custom field of an entity linked via an
  // "Expediente relacionado" field) aren't fields of this form - reference
  // them by their stable output_name rather than their internal composite id.
  // normalizeRef must not run on it: it strips dots, which would turn a
  // dotted output_name like "carro.estado" into "carroestado", a reference
  // the backend can't resolve.
  const field_reference = resolveFieldReference(selectedValue);

  return {
    field_reference,
    field_reference_type: selectedValue?.type,
    condition_type: selectedOperation?.name,
    operator: field.conditionsOperator || "OR",
    value,
  };
}

function formatDateBound(
  date: string | undefined | null,
  includeTime: boolean | undefined,
): string | null {
  if (!date) return null;
  if (date === "now") return "now";
  return includeTime
    ? dayjs(date, "DD/MM/YYYY").format("DD/MM/YYYY HH:mm")
    : dayjs(date, "DD/MM/YYYY").format("DD/MM/YYYY");
}

function applyYearPrecisionConfig(field: FormField, config: Config): void {
  config.year_range_type = field.useRelativeDate ? "relative" : "fixed";
  if (!field.useRelativeDate) {
    config.min_year = field.minYear ?? null;
    config.max_year = field.maxYear ?? null;
    return;
  }
  config.year_offset_min =
    field.relativeMinDate?.adjustment === "before"
      ? -Number(field.relativeMinDate?.value)
      : Number(field.relativeMinDate?.value);
  config.year_offset_max =
    field.relativeMaxDate?.adjustment === "after"
      ? Number(field.relativeMaxDate?.value)
      : -Number(field.relativeMaxDate?.value);
}

function applyRelativeDateConfig(field: FormField, config: Config): void {
  config.is_relative_max = field.useRelativeDate;
  config.relative_max_value =
    field?.relativeMaxDate?.adjustment === "after"
      ? Number(field?.relativeMaxDate?.value)
      : Number(field?.relativeMaxDate?.value) * -1;
  config.relative_max_unit = field?.relativeMaxDate?.unit;
  config.relative_max_business_days = false;

  config.is_relative_min = field.useRelativeDate;
  config.relative_min_value =
    field?.relativeMinDate?.adjustment === "after"
      ? Number(field?.relativeMinDate?.value)
      : Number(field?.relativeMinDate?.value) * -1;
  config.relative_min_unit = field?.relativeMinDate?.unit;
  config.relative_min_business_days = false;
}

function applyDateTypeConfig(field: FormField, config: Config): void {
  if (isYearPrecision(field.type, field)) {
    applyYearPrecisionConfig(field, config);
    return;
  }

  if (field.useRelativeDate) {
    applyRelativeDateConfig(field, config);
  }

  if (field.includeTime) {
    config.min_datetime = formatDateBound(field.minDate, true);
    config.max_datetime = formatDateBound(field.maxDate, true);
  } else {
    config.min_date = formatDateBound(field.minDate, false);
    config.max_date = formatDateBound(field.maxDate, false);
  }

  config.time_format = field.format24Hour ? "24h" : "12h";
  config.includeTime = field.includeTime;
}

function applyTextLikeConfig(field: FormField, config: Config): void {
  switch (field.type) {
    case "text":
      config.input_type = "text";
      config.max_length = field.maxLength || null;
      config.mask = field.mask || undefined;
      break;
    case "number":
      config.allow_negative = false;
      config.allow_decimals = allowDecimalsFormat(field.precision ?? null);
      config.precision = decimalsFormat(field.precision);
      config.min = field.minValue;
      config.max = field.maxValue;
      config.number_format = field.numberFormat ?? "normal";
      break;
    case "textarea":
      config.max_length = field.maxLength || null;
      break;
    case "email":
      config.max_length = field.maxLength || null;
      config.allowed_domains = field.allowedDomains || null;
      config.blocked_domains = field.disallowedDomains || null;
      break;
  }
}

function applySelectTypeConfig(field: FormField, config: Config): void {
  config.options_type = field.valueSource;

  if (field.valueSource === "manual") {
    config.options = (field.options || []).map((option) => ({
      value: normalizeRef(option),
      label: option,
    }));
  } else if (field.valueSource === "database") {
    config.data_center_node_id = field.variableRef?.groupId ?? null;
    config.data_center_node = field.variableRef?.groupLabels?.[0] ?? null;
    config.data_center_table = field.variableRef?.groupLabels?.[1] ?? null;
    config.data_center_column = field.variableRef?.itemValue ?? null;
    config.data_center_value_type = field.variableRef?.type ?? null;
    config.is_var_by_registry = field.is_var_by_registry;
    config.source = (field.variableRef?.source || "sdt").toUpperCase();
  }

  config.searchable = true;
  config.multiple = !!field.multiple;
}

function buildFieldConfig(field: FormField): Config {
  const config: Config = {};

  switch (field.type) {
    case "text":
    case "email":
    case "textarea":
    case "number":
      applyTextLikeConfig(field, config);
      break;
    case "select":
      applySelectTypeConfig(field, config);
      break;
    case "date":
    case "datetime":
    case "date_range":
    case "datetime_range":
      applyDateTypeConfig(field, config);
      break;
    case "file":
      config.allowed_mimes = field.allowAnyFile ? ["*"] : field.fileTypes || [];
      config.multiple = field.multipleFiles || false;
      if (field.fileSizeKey) {
        config.max_total_size_mb = field.maxFileSize;
      } else {
        config.max_file_size_mb = field.maxFileSize;
      }
      break;
    case "phone":
      config.allow_extensions = field.allowCountry;
      break;
    case "identification":
      config.document_type = field.documentType;
      config.allow_select_document_type = field.allowUserChooseDocType;
      config.isNitHomologado = field.isNitHomologado;
      break;
    case "interoperability":
      config.service = field.service;
      config.action = field.action;
      config.input_field = field.input_field;
      config.output_values = field.output_values;
      break;
    case "switch":
      config.label = field.label;
      break;
    case "expedient":
      config.expedient_base_entity_id = field.expedientBaseEntityId || null;
      config.additional_columns = field.additionalColumns;
      config.confirm_reselection = field.confirm_reselection || false;
      config.attach_to_formality_request =
        field.attachToFormalityRequest || false;
      break;
    case "filtered-list":
      config.levels = field.levels || [];
      break;
  }

  return config;
}

export function buildFormattedField(
  field: FormField,
  fieldIndex: number,
  form: FormPage[],
  validFieldRefs: Set<string>,
): BaseField {
  const mappedType = mapFieldType(field);

  const mappedConditions = (field.conditions ?? [])
    .map((c) => ({
      condition: mapConditionToField(c, field, form),
      // Conditions built from an external variable (e.g. a custom field of an
      // entity linked via an "Expediente relacionado" field, or any other
      // workflow node variable) carry a node_id and/or output_name and don't
      // correspond to a field in this same form, so they can't be checked
      // against validFieldRefs - only self-references to sibling form fields
      // can. node_id alone isn't reliable: it doesn't survive a save/reload
      // round trip (the backend's condition schema has no node_id field), so
      // output_name - which deformatter.ts always restores for externals -
      // must also count.
      isExternalVariable: !!(
        c.data.selectedValue?.node_id || c.data.selectedValue?.output_name
      ),
    }))
    .filter(
      ({ condition, isExternalVariable }) =>
        isExternalVariable || validFieldRefs.has(condition.field_reference),
    )
    .map(({ condition }) => condition);

  const hasValidConditions = mappedConditions.length > 0;

  return {
    name: field.technicalKey,
    label: field.name,
    id: field.id?.includes("field") ? null : field.id,
    type: fieldTypeConverter(
      mappedType,
      field.includeTime,
      field.datePrecision,
    ),
    placeholder: field.placeholder || "",
    required: field.required || false,
    order: fieldIndex + 1,
    default_value: defaultValueConverter(mappedType, field),
    config: buildFieldConfig(field),
    is_conditional: hasValidConditions,
    conditions: hasValidConditions ? mappedConditions : [],
    is_read_only: field.isReadOnly,
    is_preventable: field.isPreventable,
    is_array: field.allowMultipleValues || false,
    max_items:
      field.allowMultipleValues && field.allowedQuantityOfValues
        ? Number(field.allowedQuantityOfValues)
        : null,
  };
}

const mapFieldType = (field: FormField) => {
  switch (field.type) {
    case "identification":
      return "dui_nit";
    case "switch":
      return "switch";
    case "expedient":
      return "expedient";
    case "filtered-list":
      return "filtered_select";
    default:
      return field.type;
  }
};
